import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { birimAgaci, type BirimDugumu } from '../api/organizasyon';
import { HataKutusu } from '../bilesenler/temel';
import { yetkiKullan } from '../yetki/yetki';

/**
 * BİRİM AĞACI.
 *
 * Kapalı birimler VARSAYILAN OLARAK GİZLİ. Birim silinmiyor, kapatılıyor;
 * kapalı olanlar da gösterilseydi 34 müdürlüğün listesi zamanla geçmişin
 * arşiviyle dolar ve güncel yapı okunamaz hale gelirdi.
 */
export function BirimAgaci() {
  const [kapaliDahil, kapaliDahilYaz] = useState(false);

  const agac = useQuery({
    queryKey: ['birim-agaci', kapaliDahil],
    queryFn: () => birimAgaci(kapaliDahil),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Birim ağacı</h1>
          <p className="mt-1 text-sm text-slate-600">
            Kurumsal yapı, altı kademe. Personel sayıları alt birimleri kapsamaz.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={kapaliDahil}
            onChange={(olay) => kapaliDahilYaz(olay.target.checked)}
            className="rounded border-slate-300"
          />
          Kapatılmış birimleri de göster
        </label>
      </div>

      <HataKutusu hata={agac.error} />

      {agac.isPending && <p className="text-sm text-slate-500">Yükleniyor…</p>}

      <div className="rounded-lg border border-slate-200 bg-white p-2">
        {agac.data?.map((kok) => (
          <Dugum key={kok.birimId} birim={kok} derinlik={0} />
        ))}
        {agac.data?.length === 0 && (
          <p className="p-4 text-sm text-slate-500">Görüntüleyebileceğiniz birim yok.</p>
        )}
      </div>
    </div>
  );
}

const OPERASYON_ETIKETI: Record<string, string> = {
  saha: 'Saha',
  yari_saha: 'Yarı saha',
  buro: 'Büro',
};

function Dugum({ birim, derinlik }: { birim: BirimDugumu; derinlik: number }) {
  // Müdürlük seviyesine (K3) kadar açık başlar; altı kapalı. 34 müdürlüğün
  // tüm ekipleri açık gelseydi ilk ekranda yüzlerce satır olurdu.
  const [acik, acikYaz] = useState(derinlik < 3);
  const altVarMi = birim.altBirimler.length > 0;
  const gezin = useNavigate();

  /*
   * BİRİM AĞACI `BIRIM_YONET` İLE DE AÇILIYOR, PERSONEL LİSTESİ AÇILMIYOR.
   *
   * `BIRIM_YONET` yetkili ama `PERSONEL_GORUNTULE` olmayan kullanıcı ağacı
   * görür; sayı düğmesi tıklanabilir olsaydı boş liste ve 403 alırdı.
   * Yetki yoksa düz metne dönüyor.
   */
  const personelGorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'PERSONEL_YONET');

  return (
    <div>
      <div
        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50"
        style={{ paddingLeft: `${derinlik * 16 + 8}px` }}
      >
        <button
          type="button"
          onClick={() => acikYaz((o) => !o)}
          disabled={!altVarMi}
          className="w-4 shrink-0 text-xs text-slate-400 disabled:opacity-0"
        >
          {acik ? '▾' : '▸'}
        </button>

        <span className="min-w-0 flex-1 truncate text-sm text-slate-900">
          {birim.ad}
          <span className="ml-2 text-xs text-slate-400">{birim.kademeKod}</span>
        </span>

        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
          {OPERASYON_ETIKETI[birim.operasyonTipi] ?? birim.operasyonTipi}
        </span>

        {/* Yöneticisi olmayan birim plan ve onay zincirini kırar; uyarı erken görünmeli. */}
        {!birim.yoneticiVarMi && birim.kademeSeviye >= 3 && (
          <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
            yönetici yok
          </span>
        )}

        {birim.gecerlilikBitis && (
          <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
            kapalı
          </span>
        )}

        {personelGorebilir ? (
          <button
            type="button"
            onClick={() => void gezin(`/personel?birimId=${birim.birimId}`)}
            className="shrink-0 rounded px-1.5 py-0.5 text-xs text-kurum-600 hover:bg-kurum-50"
            title="Bu birimdeki personeli listele"
          >
            {birim.personelSayisi} kişi
          </button>
        ) : (
          <span className="shrink-0 px-1.5 py-0.5 text-xs text-slate-500">
            {birim.personelSayisi} kişi
          </span>
        )}
      </div>

      {acik &&
        birim.altBirimler.map((alt) => (
          <Dugum key={alt.birimId} birim={alt} derinlik={derinlik + 1} />
        ))}
    </div>
  );
}
